'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../lib/cn'

export interface GlowCenterProps {
  /**
   * Color source from theme variables
   * @default 'primary'
   */
  color?: 'primary' | 'accent' | 'muted'

  /**
   * Enable "light turning on" entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * GlowCenter - Radial gradient glow emanating from center
 *
 * Creates a soft, theme-aware gradient glow effect that works in both
 * light and dark modes. Uses OKLCH relative color syntax for theme integration.
 *
 * Fixed intensity: Light 0.10, Dark 0.11
 *
 * @example
 * ```tsx
 * // Static
 * <GlowCenter />
 *
 * // With entrance animation
 * <GlowCenter animate />
 * ```
 */
export function GlowCenter({
  color = 'primary',
  animate = true,
  className,
}: GlowCenterProps) {
  const prefersReducedMotion = useReducedMotion()
  const colorVar = color === 'muted' ? '--muted-foreground' : `--${color}`

  const lightStyle = {
    background: `radial-gradient(
      ellipse 60% 60% at 50% 50%,
      oklch(from var(${colorVar}) calc(l - 0.1) c h / 0.10),
      transparent 70%
    )`,
  }

  const darkStyle = {
    background: `radial-gradient(
      ellipse 60% 60% at 50% 50%,
      oklch(from var(${colorVar}) calc(l - 0.1) c h / 0.11),
      transparent 70%
    )`,
  }

  // Static version
  if (!animate) {
    return (
      <>
        <div
          className={cn('absolute inset-0 z-0 dark:hidden', className)}
          style={lightStyle}
        />
        <div
          className={cn('absolute inset-0 z-0 hidden dark:block', className)}
          style={darkStyle}
        />
      </>
    )
  }

  // Animated version
  return (
    <>
      <motion.div
        className={cn('absolute inset-0 z-0 origin-center dark:hidden', className)}
        style={lightStyle}
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: prefersReducedMotion ? 0.3 : 1,
          ease: [0.16, 1, 0.3, 1],
        }}
      />
      <motion.div
        className={cn('absolute inset-0 z-0 origin-center hidden dark:block', className)}
        style={darkStyle}
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: prefersReducedMotion ? 0.3 : 1,
          ease: [0.16, 1, 0.3, 1],
        }}
      />
    </>
  )
}
